import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { datasetsAPI } from "../../utils/datasetsAPI";
import { analyticsAPI } from "../../utils/analyticsAPI";
import { reportsAPI } from "../../utils/reportsAPI";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner, SkeletonTable } from "../../components/Loaders";
import { ErrorState } from "../../components/EmptyStates";
import Pagination from "../../components/Pagination";

function AssignedClients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadClients(currentPage);
  }, [currentPage]);

  const loadClients = async (page = 1) => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getAccessible();
      const data = response?.data || response;
      const clientsList = Array.isArray(data) ? data : data?.results || [];

      // Simple client-side pagination
      const pageSize = 10;
      const startIdx = (page - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      const paginatedClients = clientsList.slice(startIdx, endIdx);

      setClients(paginatedClients);
      setPagination({
        current_page: page,
        total_pages: Math.ceil(clientsList.length / pageSize),
        total_items: clientsList.length,
        page_size: pageSize,
        has_next: endIdx < clientsList.length,
        has_previous: page > 1,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadClientDetails = async (client) => {
    try {
      setDetailsLoading(true);
      const [datasetsRes, analyticsRes, reportsRes] = await Promise.all([
        datasetsAPI.list(client.id, { page_size: 100 }),
        analyticsAPI.getDashboard(client.id).catch(() => ({ data: {} })),
        reportsAPI
          .list(client.id, { page_size: 100 })
          .catch(() => ({ data: [] })),
      ]);

      const datasetsData =
        datasetsRes?.data || (Array.isArray(datasetsRes) ? datasetsRes : []);
      const analyticsData = analyticsRes?.data || analyticsRes || {};
      const reportsData =
        reportsRes?.data || (Array.isArray(reportsRes) ? reportsRes : []);

      setClientDetails({
        datasets: Array.isArray(datasetsData) ? datasetsData.length : 0,
        analytics: analyticsData,
        reports: Array.isArray(reportsData) ? reportsData : [],
        activeAnalyses: 0, // TODO: Add analysis count when analysis API is ready
      });
    } catch (err) {
      console.error("Failed to load client details:", err);
      setClientDetails({
        datasets: 0,
        analytics: {},
        reports: [],
        activeAnalyses: 0,
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientDetails(null);
    loadClientDetails(client);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="card">
          <SkeletonTable rows={5} cols={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <ErrorState error={error} onRetry={() => loadClients(currentPage)} />
      </div>
    );
  }

  if (selectedClient) {
    return (
      <div className="page-content">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedClient(null)}
          style={{ marginBottom: "16px" }}
        >
          ← Back to Clients
        </button>

        <div className="card">
          <h2 className="card-title">{selectedClient.name}</h2>
          <p style={{ color: "var(--text-grey)" }}>
            Contact: {selectedClient.contact_email || "N/A"} | Phone:{" "}
            {selectedClient.contact_phone || "N/A"}
          </p>

          {detailsLoading ? (
            <LoadingSpinner />
          ) : clientDetails ? (
            <>
              <div className="stats-grid" style={{ marginTop: "16px" }}>
                <div className="stat-card">
                  <div className="stat-label">Datasets</div>
                  <div className="stat-value">{clientDetails.datasets}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Reports</div>
                  <div className="stat-value">
                    {clientDetails.reports.length}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Pending Reports</div>
                  <div className="stat-value">
                    {clientDetails.reports.filter((r) => !r.submitted).length}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Rows</div>
                  <div className="stat-value">
                    {clientDetails.analytics?.dataset_stats?.total_rows || 0}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    navigate(`/analyst/data-tables?org=${selectedClient.id}`)
                  }
                >
                  View Data Tables
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/analyst/analysis?org=${selectedClient.id}`)
                  }
                >
                  Create Analysis
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/analyst/design-report?org=${selectedClient.id}`)
                  }
                >
                  Design Report
                </button>
              </div>

              {clientDetails.reports.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                  <h3>Recent Reports</h3>
                  <table className="data-table" style={{ marginTop: "12px" }}>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientDetails.reports.slice(0, 5).map((report) => (
                        <tr key={report.id}>
                          <td>{report.title}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                report.submitted
                                  ? "status-finished"
                                  : "status-urgent"
                              }`}
                            >
                              {report.submitted ? "Submitted" : "Draft"}
                            </span>
                          </td>
                          <td>
                            {new Date(report.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="card">
        <h2 className="card-title">Assigned Clients</h2>
        <p style={{ color: "var(--text-grey)", marginBottom: "16px" }}>
          Organizations you have access to as an analyst
        </p>
        <table className="data-table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Contact Email</th>
              <th>Contact Phone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length > 0 ? (
              clients.map((client) => (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td>{client.contact_email || "N/A"}</td>
                  <td>{client.contact_phone || "N/A"}</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSelectClient(client)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="4"
                  style={{
                    textAlign: "center",
                    padding: "24px",
                    color: "var(--text-grey)",
                  }}
                >
                  No assigned clients found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {pagination && pagination.total_pages > 1 && (
          <Pagination pagination={pagination} onPageChange={handlePageChange} />
        )}
      </div>
    </div>
  );
}

export default AssignedClients;
